import { FormEvent, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { parentApi } from '../../lib/parentApi';
import {
  Badge,
  Button,
  EmptyTableRow,
  ListToolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea,
  Tile,
  TileContent,
  TileHeader,
  TileTitle
} from '../../components/ui';

function todayDate() {
  return new Date().toISOString().slice(0, 10);
}

const PERIOD_OPTIONS = Array.from({ length: 12 }, (_, index) => index + 1);

export default function ParentExcuses() {
  const [selectedUnid, setSelectedUnid] = useState('');
  const [validFrom, setValidFrom] = useState(todayDate());
  const [validTo, setValidTo] = useState(todayDate());
  const [kind, setKind] = useState<'sick' | 'excuse' | 'beurlaubung'>('sick');
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [selectedPeriods, setSelectedPeriods] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const { data: children = [] } = useQuery(['parent-children'], () => parentApi.children());
  const { data: excuses = [], refetch } = useQuery(
    ['parent-excuses', selectedUnid],
    () => parentApi.excuses(selectedUnid),
    { enabled: Boolean(selectedUnid) }
  );

  const selectedChild = useMemo(
    () => children.find((child) => child.sophomorixUnid === selectedUnid) || null,
    [children, selectedUnid]
  );

  const togglePeriod = (periodNo: number) => {
    setSelectedPeriods((current) =>
      current.includes(periodNo)
        ? current.filter((value) => value !== periodNo)
        : [...current, periodNo].sort((a, b) => a - b)
    );
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedUnid) {
      toast.error('Bitte zuerst ein Kind auswählen.');
      return;
    }
    if (validTo < validFrom) {
      toast.error('Das Enddatum darf nicht vor dem Startdatum liegen.');
      return;
    }
    setSaving(true);
    try {
      let documentFileId: number | null = null;
      if (file) {
        documentFileId = await parentApi.uploadExcuseFile(selectedUnid, file);
      }
      await parentApi.createExcuse({
        student_unid: selectedUnid,
        kind,
        valid_from: validFrom,
        valid_to: validTo,
        reason,
        document_file_id: documentFileId,
        excuse_date: validFrom === validTo ? validFrom : null,
        affected_periods:
          validFrom === validTo && selectedPeriods.length > 0
            ? selectedPeriods.map((periodNo) => ({ period_no: periodNo, date: validFrom }))
            : null
      });
      toast.success('Entschuldigung wurde eingereicht.');
      setReason('');
      setFile(null);
      setSelectedPeriods([]);
      await refetch();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Entschuldigung konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <ListToolbar
        title="Entschuldigungen"
        description="Krankmeldungen für ein oder mehrere Tage sowie optional einzelne Stunden."
      />

      <Tile>
        <TileHeader>
          <TileTitle>Neue Krankmeldung</TileTitle>
        </TileHeader>
        <TileContent>
          <form className="grid grid-cols-1 gap-3 md:grid-cols-2" onSubmit={handleSubmit}>
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Kind
              <select
                value={selectedUnid}
                onChange={(event) => setSelectedUnid(event.target.value)}
                className="input-field mt-1 min-h-[2.25rem] py-2 text-sm"
                required
              >
                <option value="">Bitte auswählen</option>
                {children.map((child) => (
                  <option key={child.sophomorixUnid} value={child.sophomorixUnid}>
                    {child.Vorname} {child.Name} ({child.Klasse})
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300">
              Typ
              <select
                value={kind}
                onChange={(event) => setKind(event.target.value as 'sick' | 'excuse' | 'beurlaubung')}
                className="input-field mt-1 min-h-[2.25rem] py-2 text-sm"
              >
                <option value="sick">Krankmeldung</option>
                <option value="excuse">Entschuldigung</option>
                <option value="beurlaubung">Beurlaubung</option>
              </select>
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300">
              Von
              <input
                type="date"
                className="input-field mt-1 min-h-[2.25rem] py-2 text-sm"
                value={validFrom}
                onChange={(event) => setValidFrom(event.target.value)}
                required
              />
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300">
              Bis
              <input
                type="date"
                className="input-field mt-1 min-h-[2.25rem] py-2 text-sm"
                value={validTo}
                onChange={(event) => setValidTo(event.target.value)}
                required
              />
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300 md:col-span-2">
              Grund
              <textarea
                className="input-field mt-1 min-h-[5rem] py-2 text-sm"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Optionaler Hinweis für die Schule"
              />
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300 md:col-span-2">
              Ärztliches Attest (optional)
              <input
                type="file"
                className="input-field mt-1 min-h-[2.25rem] py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary-600 file:px-3 file:py-1.5 file:text-sm file:text-white"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
            </label>

            {validFrom === validTo ? (
              <div className="md:col-span-2">
                <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">Einzelne Stunden (optional)</p>
                <div className="flex flex-wrap gap-2">
                  {PERIOD_OPTIONS.map((periodNo) => (
                    <Button
                      key={periodNo}
                      type="button"
                      size="sm"
                      variant={selectedPeriods.includes(periodNo) ? 'primary' : 'outline'}
                      onClick={() => togglePeriod(periodNo)}
                    >
                      {periodNo}. Std
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="md:col-span-2">
              <Button type="submit" disabled={saving}>
                {saving ? 'Speichert...' : 'Entschuldigung absenden'}
              </Button>
            </div>
          </form>
        </TileContent>
      </Tile>

      <TableScrollArea>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Kind</TableHeaderCell>
              <TableHeaderCell>Zeitraum</TableHeaderCell>
              <TableHeaderCell>Typ</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!selectedUnid ? <EmptyTableRow colSpan={4} title="Bitte ein Kind auswählen." /> : null}
            {selectedUnid && excuses.length === 0 ? (
              <EmptyTableRow colSpan={4} title="Noch keine Entschuldigungen vorhanden." />
            ) : null}
            {excuses.map((entry: any) => (
              <TableRow key={entry.id}>
                <TableCell>{selectedChild ? `${selectedChild.Vorname} ${selectedChild.Name}` : '-'}</TableCell>
                <TableCell>
                  {entry.valid_from} - {entry.valid_to}
                </TableCell>
                <TableCell>{entry.kind}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      entry.approval_status === 'approved'
                        ? 'success'
                        : entry.approval_status === 'rejected'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {entry.approval_status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableScrollArea>
    </div>
  );
}
